import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Play, ArrowRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { getJSON } from "@/lib/api";
import { youTubeThumb } from "@/lib/youtube";
import { VIDEO, POSTER } from "@/lib/images";

const fmtDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : "";

function Tile({ post, featured }) {
  const thumb = youTubeThumb(post.youtube_url);
  return (
    <Link
      to={`/labs/${post.slug}`}
      className={`group flex flex-col overflow-hidden rounded-2xl border border-black/10 bg-white transition-shadow duration-300 hover:shadow-[0_28px_60px_-30px_rgba(30,34,37,0.4)] ${
        featured ? "md:col-span-2 md:flex-row" : ""
      }`}
      data-testid={`labs-tile-${post.slug}`}
    >
      <div
        className={`relative overflow-hidden bg-[var(--bg-contrast)] ${
          featured ? "aspect-video md:w-1/2" : "aspect-video"
        }`}
      >
        {thumb && (
          <img
            src={thumb}
            alt={post.title}
            loading="lazy"
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
          />
        )}
        <span className="absolute inset-0 flex items-center justify-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-black/55 text-white backdrop-blur-sm transition-transform duration-300 group-hover:scale-110">
            <Play size={22} className="ml-0.5" fill="currentColor" />
          </span>
        </span>
        {post.category && (
          <span className="absolute left-4 top-4 rounded-full bg-[var(--charcoal)]/85 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-white backdrop-blur-sm">
            {post.category}
          </span>
        )}
      </div>
      <div className={`flex flex-1 flex-col p-6 ${featured ? "md:justify-center md:p-10" : ""}`}>
        <h3
          className={`font-semibold leading-tight tracking-[-0.01em] text-[var(--ink)] ${
            featured ? "text-2xl md:text-3xl" : "text-xl"
          }`}
        >
          {post.title}
        </h3>
        {post.excerpt && (
          <p className="mt-3 flex-1 text-[15px] leading-relaxed text-[var(--muted)]">
            {post.excerpt}
          </p>
        )}
        <p className="mt-5 text-[13px] text-[var(--muted)]">
          {post.author}
          {post.read_time ? ` · ${post.read_time}` : ""}
          {post.published_at ? ` · ${fmtDate(post.published_at)}` : ""}
        </p>
      </div>
    </Link>
  );
}

export default function Labs() {
  const [posts, setPosts] = useState([]);
  const [cats, setCats] = useState([]);
  const [active, setActive] = useState("All");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      getJSON("/labs").catch(() => []),
      getJSON("/labs/categories").catch(() => []),
    ]).then(([p, c]) => {
      setPosts(p);
      setCats(c);
      setLoading(false);
    });
  }, []);

  const filtered =
    active === "All" ? posts : posts.filter((p) => p.category === active);
  const [featured, ...rest] = filtered;

  return (
    <>
      <section className="relative overflow-hidden bg-[var(--charcoal)] pb-16 pt-36 text-white md:pb-20 md:pt-44" data-testid="labs-hero">
        <video
          className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-45"
          src={VIDEO.labs}
          poster={POSTER.labs}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          aria-hidden="true"
        />
        <div
          className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[var(--charcoal)] via-[var(--charcoal)]/75 to-[var(--charcoal)]/55"
          aria-hidden="true"
        />
        <div className="relative mx-auto max-w-7xl px-6 md:px-12">
          <Reveal>
            <span className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--mint)]">
              The AI Dept. Labs
            </span>
          </Reveal>
          <Reveal delay={80}>
            <h1 className="mt-6 t-hero max-w-4xl">
              What we're testing, building, and learning.
            </h1>
          </Reveal>
          <Reveal delay={140}>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-white/70">
              Short, honest videos on the AI tools and plays we run for clients — no hype, just what's working.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-[var(--bg-base)] py-14 md:py-20">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          {cats.length > 0 && (
            <div className="flex flex-wrap gap-2.5" data-testid="labs-filters">
              {["All", ...cats].map((c) => (
                <button
                  key={c}
                  onClick={() => setActive(c)}
                  className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors ${
                    active === c
                      ? "border-[var(--green-ink)] bg-[var(--green-ink)] text-white"
                      : "border-black/15 bg-white text-[var(--ink)]/70 hover:border-[var(--green-ink)]/50"
                  }`}
                  data-testid={`labs-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {c}
                </button>
              ))}
            </div>
          )}

          {loading ? (
            <p className="mt-12 text-[var(--muted)]">Loading…</p>
          ) : filtered.length === 0 ? (
            <p className="mt-12 rounded-2xl border border-dashed border-black/15 bg-white p-10 text-center text-[var(--muted)]" data-testid="labs-empty">
              No posts yet. Check back soon.
            </p>
          ) : (
            <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {featured && <Tile post={featured} featured />}
              {rest.map((p) => (
                <Tile key={p.slug} post={p} />
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
